import React, { useCallback, useEffect, useRef, useState } from 'react';
import useOutsideClick from 'use-onclickoutside';
import styled from 'styled-components';
import { Input } from '@sls-react/design-system';
import SearchIcon from '@sls-react/static/svg/SearchIcon';
import usePredictiveSearch from '@sls-react/hooks/usePredictiveSearch';
import classNames from 'classnames';
import PredictiveResultsList from '@sls-react/components/Chrome/Header/PredictiveResultsList';
import { Button } from '@sls-react/components/Form/Button';
import { useRouter } from '@sls-react/routing';

const BaseContainer = styled.div`
  display: flex;
  position: relative;
  margin: 0 16px;
  width: 50%;
  max-width: 714px;
`;

type Props = React.HTMLAttributes<HTMLDivElement>;

const CenterSearch: React.FC<Props> = props => {
  const router = useRouter();
  const [query, setQuery] = useState((router.query.q as string) ?? '');
  const [searchOpen, setSearchOpen] = useState(false);
  const [predictiveResults, onSubmit] = usePredictiveSearch(query);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const openSearch = useCallback(() => {
    if (query.length > 1) {
      setSearchOpen(true);
    }
  }, [query]);

  useEffect(() => {
    if (document.activeElement == inputRef.current) {
      openSearch();
    }

    if (query.length === 0) {
      setSearchOpen(false);
    }
  }, [openSearch, query]);

  function closeSearch() {
    setSearchOpen(false);
  }

  useOutsideClick(containerRef, closeSearch);

  function submitPredictive(event: React.FormEvent, query: string) {
    setSearchOpen(false);
    onSubmit(event, query);
  }

  return (
    <BaseContainer {...props} ref={containerRef}>
      <form
        action="/search"
        className="flex w-full"
        onSubmit={e => submitPredictive(e, query)}
        role="search"
      >
        <Input
          autoComplete="off"
          className={classNames('border-r-0 rounded-r-none', {
            'rounded-bl-none': searchOpen,
            'border-b-0': searchOpen,
          })}
          aria-label="Search for new and used gear"
          placeholder="Search for new and used gear"
          type="search"
          style={{ height: 40 }}
          name="q"
          onChange={e => setQuery(e.target.value)}
          onFocus={openSearch}
          ref={inputRef}
          value={query}
        />

        <Button
          css={`
            height: 40px;
            padding-top: 0;
            padding-bottom: 0;
          `}
          className={classNames('rounded-l-none rounded-r py-0 px-4', {
            'rounded-br-none': searchOpen,
          })}
          aria-label="Submit search"
          color="primary"
          type="submit"
        >
          <SearchIcon />
        </Button>
      </form>

      {searchOpen && (
        <PredictiveResultsList
          className="border border-grey-500"
          css={`
            position: absolute;
            top: 100%;
            left: 0;
            width: 100%;

            background-color: #fff;
            z-index: 10;
            margin: 0;
            list-style: none;
          `}
          results={predictiveResults}
        />
      )}
    </BaseContainer>
  );
};

export default CenterSearch;
